import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import type { ColumnDef } from "@tanstack/react-table";
import type { AdminSemesterListItemDTO } from "@/orval/model";
import {
  adminSemesterGetAdminSemestersForOrganization,
  getAdminSemesterGetAdminSemestersForOrganizationQueryKey,
  getAdminSemesterGetAdminSemestersForOrganizationQueryOptions,
} from "@/orval/admin-semester";
import { PortalPageHeader } from "@/components/portal-page-header";

const columns: ColumnDef<AdminSemesterListItemDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorKey: "name",
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Start Date" />,
    id: "startDate",
    accessorFn: (row) => format(parseISO(row.startDate), "MM/dd/yyyy"),
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="End Date" />,
    id: "endDate",
    accessorFn: (row) => format(parseISO(row.endDate), "MM/dd/yyyy"),
  },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Class Days" />,
    accessorKey: "classDaysCount",
  },
];

function SemestersPage() {
  const { organizationId } = Route.useParams();
  const navigate = useNavigate();

  const getSemesters = adminSemesterGetAdminSemestersForOrganization;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="Semesters"
        actions={
          <Button
            variant="default"
            onClick={() =>
              navigate({ to: "/organizations/$organizationId/admin/semesters/new", params: { organizationId } })
            }
          >
            <PlusIcon className="size-4 mr-2" /> Add Semester
          </Button>
        }
      />

      <DataTable
        columns={columns}
        queryFn={(params, signal) => getSemesters(organizationId, params, signal)}
        queryKeyFn={(params) => getAdminSemesterGetAdminSemestersForOrganizationQueryKey(organizationId, params)}
        rowLink={(row) => ({
          to: "/organizations/$organizationId/admin/semesters/$semesterId",
          params: { organizationId, semesterId: row.id },
        })}
        getRowIdFn={(row) => row.id.toString()}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/semesters")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getAdminSemesterGetAdminSemestersForOrganizationQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: SemestersPage,
});
